import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { ApplicationFacade, Ticket, TicketsService, TicketStatus } from '@ridango/components';
import { combineLatest, timer } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { BaseComponent, MtbService } from '@ridango/core';
import Env from '@ridango/core/env';
import { Capacitor } from '@capacitor/core';
import { ScreenBrightness } from '@capacitor-community/screen-brightness';
import { PrivacyScreen } from '@capacitor-community/privacy-screen';
import { AccountTicketsService } from '@ridango/components/lib/account-tickets-ngrx/services/account-tickets.service';
import { TicketQrVisibilityService } from '../../../services/ticket-qr-visibility.service';

@Component({
    selector: 'rwidget-ticket-qr',
    templateUrl: './ticket-qr.component.html',
    styleUrls: ['./ticket-qr.component.scss']
})
export class TicketQrComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() ticket: Ticket;
    @Input() isPage = false;
    @Output() qrClick: EventEmitter<void> = new EventEmitter();
    readonly assetsPath$ = this.applicationFacade.assetsPath$;
    @ViewChild('aztecContainer', { static: false }) aztecContainer: ElementRef;
    prevBrightness: number;
    showAztecCode = false;
    aztecSvgLoaded = false;
    showQRCode = false;

    constructor(
        private applicationFacade: ApplicationFacade,
        @Inject('ENV') public env: Env,
        private mtbService: MtbService,
        public ticketService: TicketsService,
        private accountTicketService: AccountTicketsService,
        private qrVisibilityService: TicketQrVisibilityService
    ) {
        super();
    }

    async ngOnInit() {
        this.showAztecCode = this.accountTicketService.showAztecCode(this.ticket);
        this.showQRCode = this.shouldShowQRCode();

        if (this.showQRCode) {
            this.qrVisibilityService.setCodeVisibility(true);
        }

        if (Capacitor.isNativePlatform()) {
            await PrivacyScreen.enable();

            const { brightness: currentBrightness } = await ScreenBrightness.getBrightness();
            this.prevBrightness = currentBrightness;

            if (this.showQRCode || this.env.BOB?.SET_FULL_BRIGHT) {
                await ScreenBrightness.setBrightness({ brightness: 1 });
            }

            if (this.env.BOB && this.showAztecCode) {
                this.subscribeAztecGeneration()
                    .pipe(takeUntil(this.componentDestroyed$))
                    .subscribe(async () => {
                        await ScreenBrightness.setBrightness({ brightness: 1 });
                        this.qrVisibilityService.setCodeVisibility(true);
                    });
            }
        }
    }

    shouldShowQRCode(): boolean {
        if (this.ticket.loaned) {
            return (
                !this.ticket.isOwner &&
                (this.ticket.cardType === 'Q' || this.ticket.cardType === 'S') &&
                !this.isTicketRefunded()
            );
        }
        return (this.ticket.cardType === 'Q' || this.ticket.cardType === 'S') && !this.isTicketRefunded();
    }

    isTicketRefunded(): boolean {
        return this.ticket.status === TicketStatus.Returned;
    }

    subscribeAztecGeneration() {
        return this.generateAztecCode(this.ticket.mtb);
    }

    private generateAztecCode(mtb: string) {
        const updateInterval = Number(this.env.BOB?.AZTEC_UPDATE_SECONDS) * 1000;
        return combineLatest([this.mtbService.getDeviceKey(), timer(0, updateInterval)]).pipe(
            switchMap(([key]) => this.mtbService.generateAztec(mtb, key)),
            takeUntil(this.componentDestroyed$),
            tap((aztecSvg) => {
                this.aztecSvgLoaded = true;
                setTimeout(() => {
                    if (this.aztecContainer && this.aztecContainer.nativeElement) {
                        this.aztecContainer.nativeElement.innerHTML = aztecSvg;
                    }
                });
            })
        );
    }

    ngOnDestroy(): void {
        this.qrVisibilityService.reset();

        if (Capacitor.isNativePlatform()) {
            PrivacyScreen.disable();

            if (this.showQRCode || this.showAztecCode || this.env.BOB?.SET_FULL_BRIGHT) {
                ScreenBrightness.setBrightness({ brightness: this.prevBrightness });
            }
        }
        super.ngOnDestroy();
    }
}
